import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useConnectGmailModal } from '../context/ConnectGmailModalContext';
import { useActiveSyncJobs, type ActiveSyncJobSummary } from '../hooks/useActiveSyncJobs';
import {
    getRecentSliceHint,
    getSyncFailureRecovery,
    getSyncStageDisplay,
} from '../utils/syncStageDisplay';
import type { SyncJob } from '../types/mailbox';
import { recordSyncIndicatorTransition } from '../utils/syncIndicatorTelemetry';

function buildAriaLabel(
    activeJobs: ActiveSyncJobSummary[],
    failedJobs: ActiveSyncJobSummary[],
): string {
    const parts: string[] = [];
    if (activeJobs.length === 1) {
        parts.push(`Syncing Gmail: ${activeJobs[0].mailboxEmail}`);
    } else if (activeJobs.length > 1) {
        parts.push(
            `Syncing ${activeJobs.length} mailboxes: ${activeJobs.map((j) => j.mailboxEmail).join(', ')}`,
        );
    }
    if (failedJobs.length === 1) {
        parts.push(`Needs attention: ${failedJobs[0].mailboxEmail}`);
    } else if (failedJobs.length > 1) {
        parts.push(
            `${failedJobs.length} mailboxes need attention: ${failedJobs.map((j) => j.mailboxEmail).join(', ')}`,
        );
    }
    return parts.length > 0 ? parts.join('. ') : 'Gmail sync status';
}

export function buildMailboxDetailTooltip(
    activeJobs: ActiveSyncJobSummary[],
    failedJobs: ActiveSyncJobSummary[],
): string {
    const lines: string[] = [];
    for (const job of activeJobs) {
        const label = job.currentStage
            ? getSyncStageDisplay(job.currentStage).label
            : 'Syncing';
        lines.push(`${job.mailboxEmail}: ${label}`);
    }
    for (const job of failedJobs) {
        const recovery = getSyncFailureRecovery(job.errorClass, job.mailboxErrorClass);
        lines.push(`${job.mailboxEmail}: ${recovery.headline}`);
    }
    return lines.join('\n');
}

function buildMixedFailureSecondary(failedJobs: ActiveSyncJobSummary[]): string {
    if (failedJobs.length === 1) {
        return `${failedJobs[0].mailboxEmail} needs attention`;
    }
    return `${failedJobs.map((j) => j.mailboxEmail).join(', ')} need attention`;
}

export function buildTransitionAnnouncement(
    prevActive: boolean,
    prevFailed: boolean,
    anyActive: boolean,
    anyFailed: boolean,
    primaryActiveEmail: string | null,
    primaryFailedEmail: string | null,
): string {
    if (!prevActive && anyActive) {
        return primaryActiveEmail
            ? `Gmail sync started for ${primaryActiveEmail}`
            : 'Gmail sync started';
    }
    if (prevActive && !anyActive && !anyFailed) {
        return 'Gmail sync complete';
    }
    if (prevActive && !anyActive && anyFailed) {
        return primaryFailedEmail
            ? `Gmail sync finished; ${primaryFailedEmail} still needs attention`
            : 'Gmail sync finished; one mailbox still needs attention';
    }
    if (!prevFailed && anyFailed && !anyActive) {
        return primaryFailedEmail
            ? `Gmail sync needs attention for ${primaryFailedEmail}`
            : 'Gmail sync needs attention';
    }
    if (!prevFailed && anyFailed && anyActive) {
        return 'One mailbox needs attention while sync continues';
    }
    if (prevFailed && !anyFailed && !anyActive) {
        return 'Gmail sync attention resolved';
    }
    return '';
}

function recentHintForSummary(job: ActiveSyncJobSummary): string | null {
    if (!job.syncJobId || !job.currentStage) {
        return null;
    }
    const pseudoJob: SyncJob = {
        id: job.syncJobId,
        mailbox_connection_id: job.mailboxId,
        current_stage: job.currentStage,
        requested_at: '',
        metrics: job.metrics,
    };
    return getRecentSliceHint(pseudoJob);
}

interface AmbientSyncIndicatorProps {
    /**
     * Keep the live region (announcements as before) but drop the button. Today below
     * 1024 px: the card's sync button already says it (phone-top-bar).
     */
    quiet?: boolean;
}

const AmbientSyncIndicator: React.FC<AmbientSyncIndicatorProps> = ({ quiet = false }) => {
    const { openModal } = useConnectGmailModal();
    const { activeJobs, failedJobs, anyActive, anyFailed } = useActiveSyncJobs();
    const prevTupleRef = useRef({ anyActive: false, anyFailed: false });
    const prevRecentHintByJobRef = useRef<Record<string, boolean>>({});
    const [announcement, setAnnouncement] = useState('');

    const showButton = !quiet && (anyActive || anyFailed);

    const primaryActive = activeJobs[0] ?? null;
    const primaryFailed = failedJobs[0] ?? null;

    const primaryRecentHint = useMemo(
        () => (primaryActive ? recentHintForSummary(primaryActive) : null),
        [primaryActive],
    );

    const activeLabel = useMemo(() => {
        if (!primaryActive?.currentStage) {
            return 'Syncing Gmail';
        }
        if (activeJobs.length > 1) {
            return `${activeJobs.length} mailboxes syncing`;
        }
        return getSyncStageDisplay(primaryActive.currentStage).label;
    }, [activeJobs.length, primaryActive]);

    const failureRecovery = useMemo(() => {
        if (!primaryFailed) {
            return null;
        }
        return getSyncFailureRecovery(
            primaryFailed.errorClass,
            primaryFailed.mailboxErrorClass ?? null,
        );
    }, [primaryFailed]);

    const ariaLabel = buildAriaLabel(activeJobs, failedJobs);
    const detailTooltip = buildMailboxDetailTooltip(activeJobs, failedJobs);
    const showDetailTooltip =
        activeJobs.length + failedJobs.length > 1 || (anyActive && anyFailed);

    useEffect(() => {
        const prev = prevTupleRef.current;
        const transitionAnnouncement = buildTransitionAnnouncement(
            prev.anyActive,
            prev.anyFailed,
            anyActive,
            anyFailed,
            primaryActive?.mailboxEmail ?? null,
            primaryFailed?.mailboxEmail ?? null,
        );

        const recentAnnouncements: string[] = [];
        for (const job of activeJobs) {
            const jobId = job.syncJobId;
            if (!jobId) {
                continue;
            }
            const hasHint = Boolean(recentHintForSummary(job));
            const hadHint = prevRecentHintByJobRef.current[jobId] ?? false;
            if (!hadHint && hasHint && job.mailboxEmail) {
                recentAnnouncements.push(`Newest mail is ready for ${job.mailboxEmail}`);
            }
            prevRecentHintByJobRef.current[jobId] = hasHint;
        }

        let recentAnnouncementTimeoutId: ReturnType<typeof setTimeout> | undefined;

        if (transitionAnnouncement) {
            setAnnouncement(transitionAnnouncement);
            if (recentAnnouncements.length > 0) {
                const recentText = recentAnnouncements.join('. ');
                recentAnnouncementTimeoutId = setTimeout(
                    () => setAnnouncement(recentText),
                    600,
                );
            }
        } else if (recentAnnouncements.length > 0) {
            setAnnouncement(recentAnnouncements.join('. '));
        }

        prevTupleRef.current = { anyActive, anyFailed };

        const mailboxIds = [
            ...activeJobs.map((j) => j.mailboxId),
            ...failedJobs.map((j) => j.mailboxId),
        ];
        const errorClass =
            primaryFailed?.errorClass ?? primaryActive?.errorClass ?? null;
        recordSyncIndicatorTransition(anyActive, anyFailed, mailboxIds, errorClass);

        return () => {
            if (recentAnnouncementTimeoutId !== undefined) {
                clearTimeout(recentAnnouncementTimeoutId);
            }
        };
    }, [
        anyActive,
        anyFailed,
        activeJobs,
        failedJobs,
        primaryActive?.mailboxEmail,
        primaryFailed?.mailboxEmail,
        primaryActive?.errorClass,
        primaryFailed?.errorClass,
        primaryRecentHint,
    ]);

    const button = showButton ? (
        <button
            type="button"
            onClick={openModal}
            className="flex items-center gap-1.5 min-w-0 max-w-[14rem] rounded-lg border px-2.5 py-1 text-[11px] font-semibold truncate transition-colors hover:opacity-90"
            style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
            }}
            aria-label={ariaLabel}
            data-testid="ambient-sync-indicator-button"
        >
            {anyActive ? (
                <FontAwesomeIcon
                    icon={faArrowsRotate}
                    className="text-amber-400 shrink-0 animate-spin"
                    aria-hidden
                />
            ) : (
                <FontAwesomeIcon
                    icon={faCircleExclamation}
                    className="text-amber-400 shrink-0"
                    aria-hidden
                />
            )}
            <span className="truncate">
                {anyActive ? activeLabel : failureRecovery?.headline ?? 'Gmail needs attention'}
            </span>
            {anyActive && anyFailed && (
                <span
                    className="text-[10px] text-theme-muted shrink-0 max-w-[6rem] truncate"
                    data-testid="ambient-sync-mixed-failure-cue"
                >
                    {buildMixedFailureSecondary(failedJobs)}
                </span>
            )}
        </button>
    ) : null;

    return (
        <div
            className="flex items-center gap-2 min-w-0 mr-2"
            data-testid="ambient-sync-indicator"
        >
            <span role="status" aria-live="polite" className="sr-only" aria-atomic="true">
                {announcement}
            </span>
            {showDetailTooltip && button ? (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detailTooltip}</span>} arrow>
                    <span className="inline-flex min-w-0">{button}</span>
                </Tooltip>
            ) : (
                button
            )}
        </div>
    );
};

export default AmbientSyncIndicator;
